import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { PasswordInput } from '@/components/PasswordInput';
import { GoogleLoginButton } from '@/components/GoogleLoginButton';
import { SocialLoginButtons } from '@/components/SocialLoginButtons';
import { BrandMark } from '@/components/Brand';
import { useAuth } from '@/contexts/AuthContext';
import { rememberNext } from '@/lib/postAuthNext';
import { detectTimezone } from '@/lib/timezones';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Full-screen registration overlay shown ON TOP of a (dimmed) creation flow.
 * Once the visitor registers, we reload the route so the flow mounts fully
 * authenticated and appears clearly.
 */
export function RegisterGate() {
  const { t, i18n } = useTranslation();
  const { register } = useAuth();
  const currentPath = typeof window !== 'undefined'
    ? window.location.pathname + window.location.search
    : '/dashboard';

  // Persist intent so the OAuth round-trip lands back on this creation flow.
  useEffect(() => { rememberNext(currentPath); }, [currentPath]);

  const [form, setForm] = useState({
    name: '', email: '', password: '',
    timezone: detectTimezone() || 'Europe/Moscow',
    marketing_consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (form.password.length < 6) { setErr(t('auth.weak_password')); return; }
    setSubmitting(true);
    try {
      const res = await register({ ...form, language: i18n.language?.startsWith('ru') ? 'ru' : 'en' });
      if (res?.verification_required) {
        if (res.email_sent === false) {
          toast.error(t('auth.verify_email_send_failed'));
        } else {
          toast.success(t('auth.verify_email_sent'));
        }
        const loginQs = new URLSearchParams({ verify_email: '1', next: currentPath });
        window.location.href = `/login?${loginQs.toString()}`;
        return;
      }
      if (res?.pending) {
        window.location.href = '/pending';
        return;
      }
      toast.success(t('common.success'));
      // Reload so the gated flow re-mounts authenticated and clears the gate.
      window.location.reload();
    } catch (e2) {
      const detail = e2?.response?.data?.detail || t('common.error');
      setErr(detail === 'email_delivery_unavailable' ? t('auth.verify_email_send_failed') : detail);
      toast.error(detail === 'email_delivery_unavailable' ? t('auth.verify_email_send_failed') : detail);
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 overflow-y-auto"
      data-testid="register-gate"
    >
      <div aria-hidden className="absolute inset-0 bg-[#1A1410]/45 backdrop-blur-[2px]" />
      <Card className="relative w-full max-w-md p-6 sm:p-8 my-8 shadow-2xl" data-testid="register-gate-card">
        <div className="flex items-center gap-2 mb-4">
          <BrandMark size={26} />
          <span className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">
            {t('register_gate.note')}
          </span>
        </div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">
          {t('register_gate.title')}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('register_gate.subtitle')}</p>

        <div className="mt-6 space-y-2.5">
          <GoogleLoginButton testid="gate-google-button" />
          <SocialLoginButtons />
        </div>
        <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="h-px flex-1 bg-border" /> {t('auth.or')} <span className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label htmlFor="gate-name">{t('common.name')}</Label>
            <Input id="gate-name" required value={form.name} onChange={onChange('name')}
                   data-testid="gate-name-input" className="mt-1" />
          </div>
          <div>
            <Label htmlFor="gate-email">{t('common.email')}</Label>
            <Input id="gate-email" type="email" autoComplete="email" required value={form.email}
                   onChange={onChange('email')} data-testid="gate-email-input" className="mt-1" />
          </div>
          <div>
            <Label htmlFor="gate-password">{t('common.password')}</Label>
            <PasswordInput id="gate-password" autoComplete="new-password" required minLength={6}
                   value={form.password} onChange={onChange('password')}
                   data-testid="gate-password-input" className="mt-1" />
          </div>
          {err && <div className="text-sm text-destructive" data-testid="gate-error">{err}</div>}
          <div className="flex items-start gap-2">
            <Checkbox id="gate-consent" checked={form.marketing_consent}
              onCheckedChange={(v) => setForm({ ...form, marketing_consent: !!v })}
              className="mt-0.5" data-testid="gate-consent-checkbox" />
            <Label htmlFor="gate-consent" className="text-xs text-muted-foreground font-normal leading-snug cursor-pointer">
              {t('consent.marketing')}
            </Label>
          </div>
          <Button type="submit" disabled={submitting} className="w-full h-11" data-testid="gate-submit-button">
            {submitting ? t('common.loading') : t('common.register')}
          </Button>
        </form>

        <div className="mt-5 text-sm text-center text-muted-foreground">
          {t('register_gate.have_account')}{' '}
          <Link to={`/login?next=${encodeURIComponent(currentPath)}`} className="text-primary font-medium" data-testid="gate-go-login">
            {t('common.login')}
          </Link>
        </div>
      </Card>
    </div>
  );
}

export default RegisterGate;
